import { useMemo } from 'react';
import {
  ContactDetailsSection,
  Contrast,
  NameRow,
  SubSection,
  SubSections,
  SubjectTitle
} from '@styles/ContactManager/HeaderStyles';
import { Row } from '@styles/StyledDiv';
import { GridCell } from '@shared/components/grid/GridCell';
import { CreditBureauScoreType, CreditBureauScoreTypeMap, EBureauType, IContactHeaderCreditScore } from '../interfaces';

interface ICreditScoreProps {
  onOpenCreditReport?: (bureauType: EBureauType , pulledFicoTypes : CreditBureauScoreType[]) => void; 
  data?: IContactHeaderCreditScore[];
  contactId?: string;
}

const bureauTypes = {
  [EBureauType.Transunion]: 1,
  [EBureauType.Experian]: 2,
  [EBureauType.Equifax]: 3,
  [EBureauType.ExperianFico]: 4,
};

export const CreditScore = ({ contactId, data, onOpenCreditReport }: ICreditScoreProps) => {
  interface IAccValue {
    titles: EBureauType[];
    fico: string[];
    types: string[];
    dti: string[];
  }

  const isScoreTypeKey = (key: string): key is keyof typeof CreditBureauScoreTypeMap => {
    return key in CreditBureauScoreTypeMap;
  };

  const formattedData = useMemo(() => {
    const accValue :IAccValue = {
      titles: [EBureauType.Transunion, EBureauType.Experian, EBureauType.Equifax],
      fico: ['\u00A0', '\u00A0', '\u00A0', '\u00A0'],
      types: ['\u00A0', '\u00A0', '\u00A0', '\u00A0'],
      dti: ['\u00A0', '\u00A0', '\u00A0', '\u00A0'],
    };

    const result = (data || []).reduce((acc, cur) => {
      Object.keys(bureauTypes).forEach((bureauTypeName: string, index: number) => {
        let bureauIndex = index;
        if (cur.bureauType === bureauTypeName) {
          if (cur.bureauType === EBureauType.ExperianFico) {
            bureauIndex = 1;
          }
          if (cur.fico) {
            acc.fico[bureauIndex] = acc.fico[bureauIndex] === '\u00A0' ? cur.fico.toString() : `${acc.fico[bureauIndex]}/${cur.fico.toString()}`;
          }

          if (cur.scoreType && isScoreTypeKey(cur.scoreType)) {
            const mappedType = CreditBureauScoreTypeMap[cur.scoreType];

            if (acc.types[bureauIndex] === '\u00A0') {
              acc.types[bureauIndex] = mappedType;
            } else {
              acc.types[bureauIndex] = `${acc.types[bureauIndex]}/${mappedType}`;
            }
          }

          if (cur.dti) {
            acc.dti[bureauIndex] = cur.dti.toString();
          }
        }
      });
      return acc;
    }, accValue);

    return result;
  }, [data]);

  const getTypesForBureau = (bureau: EBureauType): CreditBureauScoreType[] => {
    const index = formattedData.titles.indexOf(bureau);
    if (index === -1) return [];

    const value = formattedData.types[index];
    if (!value || value === '\u00A0') return [];

    return value.split('/') as unknown as CreditBureauScoreType[];
  };

  return (
    <ContactDetailsSection>
      <NameRow>
        <SubSections>
          <SubSection width="30%">
            <SubjectTitle>Credit</SubjectTitle>
          </SubSection>
          <SubSection
            width="28%"
            style={{ paddingLeft: '5px' }}
          >
            <SubjectTitle>Score</SubjectTitle>
          </SubSection>
          <SubSection
            width="28%"
            style={{ paddingLeft: '5px' }}
          >
            <SubjectTitle>Type</SubjectTitle>
          </SubSection>
          <SubSection width="16%">
            <SubjectTitle>DTI(%)</SubjectTitle>
          </SubSection>
        </SubSections>
      </NameRow>

      <SubSections>
        <SubSection width="30%">
          {formattedData.titles.map((item, index) => (
            <Row key={index}>
              <Contrast
                bold
                disabled={!contactId}
                onClick={() => onOpenCreditReport?.(item, getTypesForBureau(item))}
              >
                <GridCell text={item} />
              </Contrast>
            </Row>
          ))}
        </SubSection>

        <SubSection
          width="28%"
          style={{ paddingLeft: '5px' }}
        >
          {formattedData.fico.map((scoreItem: string, index: number) => (
            <Row key={index}>
              <GridCell text={scoreItem} />
            </Row>
          ))}
        </SubSection>
        <SubSection
          width="28%"
          style={{ paddingLeft: '5px' }}
        >
          {formattedData.types.map((dtiItem: string, index: number) => (
            <Row key={index}>
              <GridCell text={dtiItem} />
            </Row>
          ))}
        </SubSection>
        <SubSection width="16%">
          {formattedData.dti.map((dtiItem: string, index: number) => (
            <Row key={index}>
              <GridCell text={dtiItem} />
            </Row>
          ))}
        </SubSection>
      </SubSections>
    </ContactDetailsSection>
  );
};
